import { inject } from 'dependable-react';
import { isEmpty, pick } from 'lodash';
import { autorun } from 'mobx';

import { Gender } from '../enums/Gender';
import { MoreMagicType } from '../enums/MoreMagicType';
import { Avatar, Upload, Wrap } from '../state/models';
import { apify, deapify } from '../utils/apify';
import { IPayload } from '../utils/bookPayloadFormater';
import { APP_DATA_TOKEN } from './InjectTokens';

export class StorageService {
  private appData = inject(APP_DATA_TOKEN);

  public setupSave(bookId: number, orderId: number) {
    autorun(() => {
      const data = this.appData.bookPayload;
      window.localStorage.setItem(`payload_${orderId}_${bookId}`, JSON.stringify(data));
    });
  }

  public async initialFill(bookId: number, orderId: number, childName?: string) {
    return new Promise((resolve, reject) => {
      const rawData = window.localStorage.getItem(`payload_${orderId}_${bookId}`);

      try {
        if (rawData) {
          const data = deapify(JSON.parse(rawData)) as IPayload;
          const language = data.orderItem.customization.language;
          const wrapId = data.orderItem.wrap;
          const extraProducts = data.orderItem.extraProducts;
          const textVariables = apify(data.orderItem.customization.textVariables) as Record<string, string>;
          const avatarsParams = data.orderItem.customization.avatars;
          const childPhoto = data.orderItem.customization.childPhoto;
          const dedicationPhoto = data.orderItem.customization.dedicationPhoto;
          const avatars = this.appData.findAll(Avatar);
          const moreMagicDedication = this.appData.moreMagic.find(({ type }) => type === MoreMagicType.DEDICATION);
          const moreMagicIconed = this.appData.moreMagic.find(({ type }) => type === MoreMagicType.ICONED);

          // fill avatar text variable features
          avatars.forEach((avatar) => {
            const fields = avatar.fields.map(({ uid }) => uid);
            const foundParams = pick(textVariables, fields);

            if (!isEmpty(foundParams)) {
              avatar.params = foundParams;
            }
          });

          // fill avatar char data (hair, eyes...)
          avatars.forEach((avatar) => {
            const params = avatarsParams.find(({ id }) => id === avatar.id);

            if (params) {
              avatar.params = { ...avatar.params, ...params.features };

              // this will fill in the genderId from bookPayload fromat (since API doesn't need genderId...)
              if (params.features.sex) {
                avatar.genderId = params.features.sex === 'f' ? Gender.FEMALE : Gender.MALE;
              }
            }
          });

          // store child photo
          if (childPhoto) {
            const childPhotoUpload = new Upload({ ...childPhoto, type: MoreMagicType.CHILD_IMAGE });
            this.appData.add(childPhotoUpload);
          }

          // store dedication photo
          if (dedicationPhoto) {
            const dedicationPhotoUpload = new Upload({ ...dedicationPhoto, type: MoreMagicType.DEDICATION });
            this.appData.add(dedicationPhotoUpload);
          }

          if (moreMagicDedication) {
            const { dedication } = textVariables;

            if (dedication) {
              moreMagicDedication.params = { dedication: dedication.replace(/<br ?\/?>/g, '\n') };
            }
          }

          // store iconed
          if (moreMagicIconed) {
            const fields = moreMagicIconed.elements.map(({ uid }) => uid);
            const foundParams = pick(textVariables, fields);

            moreMagicIconed.params = foundParams;
          }

          // set language
          this.appData.currentLanguage = language;

          // select wrap
          if (wrapId) {
            const wrap = this.appData.findOne(Wrap, wrapId);

            if (wrap) {
              wrap.isSelected = true;
            }
          }

          // select extra products
          this.appData.extraProducts.forEach(
            (product) => (product.isSelected = Boolean(extraProducts.includes(product.id))),
          );
        }

        if (childName && this.appData.mainAvatar && !this.appData.mainAvatar.params) {
          this.appData.mainAvatar.params = {
            ...this.appData.mainAvatar.defaultPreview,
            ...(this.appData.mainAvatar.params ? this.appData.mainAvatar.params : {}),
            childs_name: childName,
          };
        }

        return resolve(true);
      } catch (e) {
        reject();
      }
    });
  }

  public removeBook(orderId: number, bookId: number) {
    window.localStorage.removeItem(`payload_${orderId}_${bookId}`);
  }
}
